import { IconButton, TableCell, TableRow, TextField, useTheme } from '@mui/material';
import { RHFAutocomplete, RHFTextField } from 'src/components/minimals/form';
import { filterOptions, upperSnakeCaseToTitleCase } from 'src/utilities';
import { CategoryData, ModalTableProps, ModalTableRowData } from './types';
import { useAuthentication } from 'src/features/authentication/context';
import { AccessRoles } from 'src/features/user-account-details/types';
import { useEffect, useState } from 'react';
import { useResponsive } from 'src/hooks/useResponsive';
import { CloseIconV2 } from 'src/assets/icons/CloseIconV2';

type AdvancedTagMappingTableRowProps = ModalTableProps & {
  rowData: ModalTableRowData[];
  tagType: string;
  index: number;
  controllerType: string | null;
};

const categoriesBasic: CategoryData = require('./data/categories.json');
const categoriesExtended: CategoryData = require('./data/categories-extended.json');

export const AdvancedTagMappingTableRow = ({
  rowData,
  index,
  handleRemoveRow,
  errors,
  customErrors,
  areTableValuesUnique,
  setValue,
  values,
  clearErrors,
  tagType,
  controllerType,
}: AdvancedTagMappingTableRowProps) => {
  const theme = useTheme();
  const {
    customerId: { value: customerId },
    siteId: { value: siteId },
    getCurrentRole,
  } = useAuthentication();

  const [categories, setSelectedCategories] = useState<CategoryData>(categoriesBasic);

  useEffect(() => {
    if (controllerType === 'storm_mode') {
      const currentCategory = values[`category-${index}`];
      const isAnotherStormModeSelected = Object.keys(values).some(
        (key) =>
          key.startsWith('category-') && key !== `category-${index}` && values[key] === 'STORM_MODE'
      );
      if (currentCategory === 'STORM_MODE') {
        setSelectedCategories(categoriesExtended);
      } else if (!isAnotherStormModeSelected) {
        setSelectedCategories(categoriesExtended);
      } else {
        setSelectedCategories(categoriesBasic);
      }
    } else {
      setSelectedCategories(categoriesBasic);
    }
  }, [controllerType, index, values]);

  const isMobile = useResponsive('down', 'sm');

  const hasReadAccess =
    getCurrentRole(customerId, siteId) === AccessRoles.SITE_MANAGER ||
    getCurrentRole(customerId, siteId) === AccessRoles.SITE_EMPLOYEE;

  const handleDropdownChange = (
    event: React.SyntheticEvent<Element, Event>,
    newValue: string | string[] | null,
    name: string
  ) => {
    if (newValue) {
      setValue(name, newValue as string);
      if (values[`name-${index}`] === '') {
        setValue(`name-${index}`, upperSnakeCaseToTitleCase(newValue as string));
        clearErrors(`name-${index}`);
      }
    } else if (newValue === null) {
      setValue(name, '');
    }
  };

  const handleValueChange = (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    areTableValuesUnique(event.target.value, index, null, null);
  };

  const getLabelForValueField = () => {
    let label = '';

    if (values.dataType === 'BOOLEAN') {
      label = index === 0 ? 'False Value' : 'True Value';
    }

    return label;
  };

  return (
    <TableRow data-sm="table-row" style={{ flexGrow: 1 }}>
      <TableCell data-sm="value-cell" sx={{ minWidth: isMobile ? '200px' : 'auto' }}>
        <RHFTextField
          name={`value-${index}`}
          testId="valueField"
          placeholder="Enter value"
          label={getLabelForValueField()}
          rules={{
            required: `Value is required!`,
          }}
          disabled={hasReadAccess}
          error={!!errors[`value-${index}`] || !!customErrors?.[`value-${index}`]}
          customHelperText={
            errors[`value-${index}`]?.message || customErrors?.[`value-${index}`]?.message?.[0]
          }
          onChange={(event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) =>
            handleValueChange(event)
          }
          type="text"
        />
      </TableCell>
      {tagType.toLowerCase() !== 'setpoint' && tagType.toLowerCase() !== 'output' ? (
        <TableCell data-sm="category-cell" sx={{ minWidth: isMobile ? '200px' : '30%' }}>
          <RHFAutocomplete
            name={`category-${index}`}
            options={categories[tagType.toLowerCase() === 'mode' ? 'mode' : 'state']?.values}
            getOptionLabel={(option: string) => {
              const { values, labels } =
                categories[tagType.toLowerCase() === 'mode' ? 'mode' : 'state'];
              const index = values.indexOf(option);
              return `${labels[index]}`;
            }}
            filterOptions={(options: string[], state: any) => {
              const { labels } = categories[tagType.toLowerCase() === 'mode' ? 'mode' : 'state'];
              return filterOptions(options, state, labels);
            }}
            isOptionEqualToValue={(option, value) => (!option ? true : option === value)}
            disabled={hasReadAccess}
            onChange={(event, newValue) =>
              handleDropdownChange(event, newValue, `category-${index}`)
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Category"
                inputProps={{
                  ...params.inputProps,
                  'data-sm': 'category-input',
                }}
              />
            )}
          />
        </TableCell>
      ) : null}
      <TableCell data-sm="name-cell" sx={{ minWidth: isMobile ? '200px' : 'auto', flexGrow: 1 }}>
        <RHFTextField
          name={`name-${index}`}
          testId="nameField"
          sx={{ position: 'relative' }}
          placeholder="Name"
          rules={
            tagType.toLowerCase() !== 'setpoint' && tagType.toLowerCase() !== 'output'
              ? {
                  required: 'Name is required!',
                }
              : undefined
          }
          disabled={hasReadAccess}
        />
      </TableCell>
      {rowData.findIndex((row) => row.index === index) > 0 &&
      !hasReadAccess &&
      values.dataType !== 'BOOLEAN' ? (
        <TableCell data-sm="remove-cell" style={{ paddingLeft: 0, width: '5%' }}>
          <IconButton onClick={() => handleRemoveRow(index)}>
            <CloseIconV2 fill={theme.palette.error.main} />
          </IconButton>
        </TableCell>
      ) : null}
    </TableRow>
  );
};
